import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LayoutService } from 'src/app/_theme/core/services/layout.service';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { extractOrgSlugFromAliasResponse } from '../core/status-main-page.url-resolver';

@Component({
  selector: 'app-status-verify-redirect',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  template: `
    <div class="status-container">
      @if (viewState === 'loading') {
        <div class="status-init-loader">
          <mat-progress-spinner [diameter]="40" mode="indeterminate"></mat-progress-spinner>
          <p>Resolving link...</p>
        </div>
      }

      @if (viewState === 'invalid') {
        <div class="status-init-loader">
          <div class="status-message-card invalid">
            <mat-icon class="material-symbols-outlined">link_off</mat-icon>
            <h2>Invalid Link</h2>
            <p>{{ invalidMessage }}</p>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      --page-bg: #f8fafc;
      --card-bg: #ffffff;
      --border: #e2e8f0;
      --text-main: #0f172a;
      --text-muted: #64748b;
      --brand-blue: #3b82f6;
      --warning: #f59e0b;
    }

    .status-container {
      min-height: 100vh;
      background: var(--page-bg);
      color: var(--text-main);
    }

    .status-init-loader {
      min-height: calc(100vh - 40px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 14px;
      color: var(--text-muted);
      padding: 20px;
      text-align: center;
    }

    .status-init-loader mat-progress-spinner {
      --mdc-circular-progress-active-indicator-color: var(--brand-blue);
    }

    .status-message-card {
      max-width: 480px;
      border: 1px solid var(--border);
      border-radius: 12px;
      background: var(--card-bg);
      padding: 22px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }

    .status-message-card h2 {
      margin: 0;
      font-size: 22px;
      font-weight: 600;
      color: var(--text-main);
    }

    .status-message-card p {
      margin: 0;
      font-size: 14px;
      color: var(--text-muted);
      line-height: 1.5;
    }

    .status-message-card mat-icon {
      width: 34px;
      height: 34px;
      font-size: 34px;
      color: var(--warning);
    }
  `]
})
export class StatusVerifyRedirectComponent implements OnInit {
  private readonly publicStatusContextKey = 'public_status_context';
  viewState: 'loading' | 'invalid' = 'loading';
  invalidMessage = 'This link is invalid or unsupported. Please use the original email link.';

  /**
   * Injects services used for routing, API calls, state management, and UI coordination.
   */
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly layoutService: LayoutService,
    private readonly statusPageService: StatusPageService
  ) { }

  /**
   * Initializes component state, derives route context, and starts first-pass data loading.
   */
  ngOnInit(): void {
    const slug = String(this.route.snapshot.paramMap.get('slug') || '').trim();
    if (!slug) {
      this.setInvalidState(this.resolveFallbackInvalidMessage());
      return;
    }

    const token = String(this.route.snapshot.queryParamMap.get('token') || '').trim();
    const queryParams = { ...this.route.snapshot.queryParams };
    queryParams.subscription_action = this.getSubscriptionAction();
    if (!token) {
      this.setInvalidState(this.buildMissingTokenMessage());
      return;
    }

    const subdomain = this.resolveSubdomain(slug);
    if (subdomain) {
      this.navigateToStatusMainPage(subdomain, slug, queryParams);
      return;
    }

    this.resolveSubdomainFromToken(token, slug).subscribe((resolvedSubdomain) => {
      if (!resolvedSubdomain) {
        this.setInvalidState(this.buildStatusPageMismatchMessage());
        return;
      }
      this.navigateToStatusMainPage(resolvedSubdomain, slug, queryParams);
    });
  }

  /**
   * Returns subscription action from the current state.
   */
  private getSubscriptionAction(): 'verify' | 'unsubscribe' {
    const action = String(this.route.snapshot.data?.['subscriptionAction'] || '').trim().toLowerCase();
    return action === 'unsubscribe' ? 'unsubscribe' : 'verify';
  }

  /**
   * Sets invalid state with an optional message override.
   */
  private setInvalidState(message?: string): void {
    this.viewState = 'invalid';
    this.invalidMessage = String(message || '').trim() || this.resolveFallbackInvalidMessage();
  }

  /**
   * Returns default invalid message for the current route context.
   */
  private resolveFallbackInvalidMessage(): string {
    const configured = String(this.route.snapshot.data?.['invalidMessage'] || '').trim();
    if (configured) {
      return configured;
    }
    const action = this.getSubscriptionAction();
    return action === 'unsubscribe'
      ? 'This unsubscribe link is invalid or unsupported. Please use the original email link.'
      : 'This verification link is invalid or unsupported. Please use the original email link.';
  }

  /**
   * Returns invalid message for missing token links.
   */
  private buildMissingTokenMessage(): string {
    const action = this.getSubscriptionAction();
    return action === 'unsubscribe'
      ? 'This unsubscribe link is missing a token. Please use the original email link.'
      : 'This verification link is missing a token. Please use the original email link.';
  }

  /**
   * Returns invalid message when the link cannot be mapped to a published page.
   */
  private buildStatusPageMismatchMessage(): string {
    const action = this.getSubscriptionAction();
    return action === 'unsubscribe'
      ? 'This unsubscribe link does not match a published status page.'
      : 'This verification link does not match a published status page.';
  }

  /**
   * Resolves subdomain using fallback rules.
   */
  private resolveSubdomain(slug: string): string {
    const fromRouteParam = String(
      this.route.snapshot.paramMap.get('org') ||
      this.route.snapshot.paramMap.get('account') ||
      ''
    ).trim().toLowerCase();
    if (fromRouteParam) {
      return fromRouteParam;
    }

    const fromQuery = String(
      this.route.snapshot.queryParamMap.get('subdomain') ||
      this.route.snapshot.queryParamMap.get('org') ||
      ''
    ).trim().toLowerCase();
    if (fromQuery) {
      return fromQuery;
    }

    const storedContext = this.getStoredPublicContext();
    const slugKey = String(slug || '').trim().toLowerCase();
    const hostKey = this.getCurrentHost();
    if (
      storedContext?.org &&
      storedContext?.slug &&
      storedContext?.host &&
      slugKey &&
      hostKey &&
      storedContext.slug === slugKey &&
      storedContext.host === hostKey
    ) {
      return storedContext.org;
    }

    const fromLayout = String(this.layoutService.getSubdomain() || '').trim().toLowerCase();
    if (fromLayout) {
      return fromLayout;
    }

    const storedSubdomain = this.getStoredSubdomain();
    if (storedSubdomain) {
      return storedSubdomain;
    }

    const host = this.getCurrentHost();
    if (!host) {
      return '';
    }

    if (host.endsWith('.localhost')) {
      const first = host.split('.')[0];
      if (first && first !== 'app' && first !== 'www') {
        return first;
      }
    }

    return '';
  }

  /**
   * Resolves subdomain from subscription token using public APIs.
   */
  private resolveSubdomainFromToken(token: string, slug: string): Observable<string> {
    const fallbackSlug = String(slug || '').trim();

    return this.statusPageService.getPublicSubscriberManageByToken(token).pipe(
      switchMap((response: any) => {
        const directSubdomain = this.extractSubdomainFromResponse(response, fallbackSlug);
        if (directSubdomain) {
          return of(directSubdomain);
        }

        const statusPageId = this.parseStatusPageId(response);
        const slugFromManage = String(response?.statusPageSlug || response?.status_page_slug || fallbackSlug).trim();
        const aliasLookup$ = this.resolveSubdomainFromAlias(slugFromManage || fallbackSlug);

        if (!statusPageId) {
          return aliasLookup$;
        }

        return this.statusPageService.getPublicStatusPageById(statusPageId).pipe(
          map((statusPageResponse: any) => this.extractSubdomainFromResponse(statusPageResponse, slugFromManage)),
          switchMap((resolvedFromStatusPage) => {
            if (resolvedFromStatusPage) {
              return of(resolvedFromStatusPage);
            }
            return aliasLookup$;
          }),
          catchError(() => aliasLookup$)
        );
      }),
      catchError(() => this.resolveSubdomainFromAlias(fallbackSlug))
    );
  }

  /**
   * Resolves subdomain from alias lookup when available.
   */
  private resolveSubdomainFromAlias(slug: string): Observable<string> {
    const alias = String(slug || '').trim();
    if (!alias) {
      return of('');
    }

    return this.statusPageService.getPublicStatusPageByAlias(alias).pipe(
      map((response: any) => this.extractSubdomainFromResponse(response, alias)),
      catchError(() => of(''))
    );
  }

  /**
   * Extracts subdomain from API payloads with slug guardrails.
   */
  private extractSubdomainFromResponse(response: any, expectedSlug: string): string {
    const normalizedExpectedSlug = String(expectedSlug || '').trim().toLowerCase();
    const candidates = [
      response,
      response?.statusPage,
      response?.status_page,
      response?.data,
      response?.body
    ];

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      const resolved = extractOrgSlugFromAliasResponse(candidate);
      const org = String(resolved?.org || '').trim().toLowerCase();
      const slug = String(resolved?.slug || '').trim().toLowerCase();
      if (!org) {
        continue;
      }
      if (!normalizedExpectedSlug || !slug || slug === normalizedExpectedSlug) {
        return org;
      }
    }

    const directOrg = String(
      response?.organizationSlug ||
      response?.organization_slug ||
      response?.org ||
      response?.organization ||
      response?.subdomain ||
      response?.account ||
      ''
    ).trim().toLowerCase();

    return directOrg;
  }

  /**
   * Parses status page id from API payloads.
   */
  private parseStatusPageId(response: any): number | null {
    const rawValue =
      response?.statusPageId ??
      response?.status_page_id ??
      response?.statusPage?.statusPageId ??
      response?.statusPage?.id ??
      response?.status_page?.statusPageId ??
      response?.status_page?.id ??
      null;

    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }

  /**
   * Navigates to status main page with subscription query params.
   */
  private navigateToStatusMainPage(subdomain: string, slug: string, queryParams: Record<string, any>): void {
    this.router.navigate(
      ['/', subdomain, 'status-main-page', slug],
      {
        queryParams,
        replaceUrl: true
      }
    );
  }

  /**
   * Returns current host from the current state.
   */
  private getCurrentHost(): string {
    const win = this.getWindow();
    if (!win?.location?.hostname) {
      return '';
    }
    return String(win.location.hostname || '').trim().toLowerCase();
  }

  /**
   * Returns stored public context from the current state.
   */
  private getStoredPublicContext(): { org: string; slug: string; host: string } | null {
    const win = this.getWindow();
    if (!win) {
      return null;
    }
    try {
      const raw = win.localStorage.getItem(this.publicStatusContextKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      const org = String(parsed?.org || '').trim().toLowerCase();
      const slug = String(parsed?.slug || '').trim().toLowerCase();
      const host = String(parsed?.host || '').trim().toLowerCase();
      if (!org || !slug || !host) {
        return null;
      }
      return { org, slug, host };
    } catch {
      return null;
    }
  }

  /**
   * Returns stored subdomain from the current state.
   */
  private getStoredSubdomain(): string {
    const win = this.getWindow();
    if (!win) {
      return '';
    }
    return String(win.localStorage.getItem('subdomain') || '').trim().toLowerCase();
  }

  /**
   * Returns window from the current state.
   */
  private getWindow(): Window | null {
    if (typeof globalThis === 'undefined') {
      return null;
    }
    return (globalThis as { window?: Window }).window ?? null;
  }
}
